import React, {
  useState, useContext, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import {
  HeadingBanner, Button, ErrorMessage,
} from 'anf-core-react';
import componentMap from './componentMap';
import CheckoutPageContext from '../../context/CheckoutPageContext';
import { useStepsContext, StepsProvider } from './StepsContext';
import validate from './helpers/stepValidation';
import useNextButtonState from './hooks/useNextButtonState';
import ContinueButton from './ContinueButton';
import useCallbackFunctionMap from './hooks/useCallbackFunctionMap';

function Steps({ schema, initialStep = 0 }) {
  const {
    currentStep,
    setCurrentStep,
    stepErrors,
    setStepError,
    clearStepError,
    completedSteps,
    setCompletedSteps,
  } = useStepsContext();
  const [allStepsCompleted, setAllStepsCompleted] = useState(
    initialStep >= schema.steps.length,
  );
  const [isStepProcessing, setIsStepProcessing] = useState(false);
  const { trigger } = useFormContext();
  const { callbackFuncionMap } = useCallbackFunctionMap;
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const isNextButtonEnabled = useNextButtonState(
    currentStep,
    schema.steps,
    componentMap,
    checkoutPageState,
  );

  const isSingleStep = schema.steps.length === 1;
  const showSummary = allStepsCompleted && !isSingleStep;

  const validateStepFields = useCallback(async (stepId) => (
    validate(stepId, schema.steps, componentMap, checkoutPageState, trigger)
  ), [schema.steps, checkoutPageState, trigger]);

  const handleNext = async () => {
    const isValid = await validateStepFields(currentStep);
    if (isValid) {
      const { onContinue = '' } = schema.steps.find((step) => step.id === currentStep);
      const callbackFn = callbackFuncionMap?.[onContinue];
      if (callbackFn) {
        setIsStepProcessing(true);
        await callbackFn();
        setIsStepProcessing(false);
      }
      clearStepError(currentStep);
      const newCompletedSteps = [...new Set([...completedSteps, currentStep])];
      setCompletedSteps(newCompletedSteps);

      const nextIncompleteStep = schema.steps.find((step) => !newCompletedSteps.includes(step.id));

      if (nextIncompleteStep) {
        setCurrentStep(nextIncompleteStep.id);
      } else {
        setAllStepsCompleted(true);
      }
    } else {
      const stepConfig = schema.steps.find((s) => s.id === currentStep);
      if (stepConfig?.errorMessage?.value) {
        setStepError(currentStep, stepConfig.errorMessage.value);
      }
    }
  };

  const handleEdit = (stepId) => {
    setAllStepsCompleted(false);
    setCurrentStep(stepId);
    clearStepError(stepId);
  };

  const renderComponentsForStep = useCallback((step) => {
    if (!step.components || !Array.isArray(step.components)) return null;
    const wasCompleted = completedSteps.includes(step.id);
    return step.components.map((componentName) => {
      const StepComponent = componentMap[componentName];
      if (!StepComponent) throw new Error(`No component found for step component: ${componentName}`);
      return (
        <StepComponent
          key={`${step.id}-${componentName}`}
          stepId={step.id}
          title={step.title}
          wasStepCompleted={wasCompleted}
          {...step.props}
        />
      );
    });
  }, [completedSteps]);

  const renderSummaryComponents = () => {
    if (!schema.summary?.components) return null;
    return schema.summary.components.map((componentName) => {
      const Component = componentMap[componentName];
      return Component ? <Component key={componentName} stepId={schema.steps[0]?.id} /> : null;
    });
  };

  const renderMobileRightRailForSteps = () => {
    const Component = componentMap.MobileCheckoutRightRailWrapper;
    return Component ? <Component key={`step-right-rail-${Component.name}`} stepId={currentStep} /> : null;
  };

  const renderFooter = () => {
    const Component = componentMap.FooterRightRailWrapper;
    return Component ? (
      <Component
        key={`footer-right-rail-${Component.name}`}
        stepId={currentStep}
      />
    ) : null;
  };

  const renderAlwaysDisplay = () => {
    if (!schema.alwaysDisplay?.components) return null;
    return schema.alwaysDisplay.components.map((componentName) => {
      const Component = componentMap[componentName];
      return Component ? <Component key={`always-${componentName}`} stepId={currentStep} /> : null;
    });
  };

  const renderOverviewForStep = (stepId) => {
    const step = schema.steps.find((s) => s.id === stepId);
    if (!step?.overviewComponent) return null;
    const OverviewComponent = componentMap[step.overviewComponent];
    return OverviewComponent ? <OverviewComponent key={`${step.id}-overview`} stepId={stepId} /> : null;
  };

  return (
    <div data-testid="steps-component" className="steps-component">
      {showSummary ? (
        <>
          {schema.steps.map((step) => (
            <div key={step.id} className="step-wrapper completed">
              <HeadingBanner variant="boxed">
                <div className="heading-banner-content">
                  <h2>{step.title?.value || step.name}</h2>
                  <Button variant="borderless" onClick={() => handleEdit(step.id)}>Edit</Button>
                </div>
              </HeadingBanner>
              <div className="step-content-wrapper">
                <div className="step-content">{renderOverviewForStep(step.id)}</div>
              </div>
            </div>
          ))}
          <div className="always-display">{renderAlwaysDisplay()}</div>
          <div className="summary-content">{renderSummaryComponents()}</div>
          {schema.summary?.submitButton
            && componentMap[schema.summary.submitButton] && (
              <div className="submit-button-container">
                {React.createElement(
                  componentMap[schema.summary.submitButton],
                  { stepId: schema.steps[0]?.id },
                )}
              </div>
          )}
          <div className="step-footer">{renderFooter()}</div>
        </>
      ) : (
        <>
          {schema.steps.map((s) => {
            const isCurrent = s.id === currentStep;
            const isCompleted = completedSteps.includes(s.id);
            return (
              <div key={s.id} className={`step-wrapper ${isCurrent ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                {s.title && (
                  <HeadingBanner variant="boxed">
                    <div className="heading-banner-content">
                      <h2>{s.title.value}</h2>
                      {isCompleted && !isCurrent && (
                        <Button variant="borderless" onClick={() => handleEdit(s.id)}>Edit</Button>
                      )}
                    </div>
                  </HeadingBanner>
                )}
                {isCurrent && (
                  <div className="step-content-wrapper">
                    <div className="step-content">{renderComponentsForStep(s)}</div>
                    <div className="step-footer">
                      {stepErrors[currentStep] && <ErrorMessage id={`step-error-${currentStep}`}>{stepErrors[currentStep]}</ErrorMessage>}
                      <div className="step-actions">
                        {isSingleStep ? (
                          schema.summary?.submitButton && React.createElement(componentMap[schema.summary.submitButton], { stepId: currentStep, 'data-testid': 'submit-button' })
                        ) : (
                          <ContinueButton
                            onContinue={handleNext}
                            step={s}
                            isEditing={isCompleted && isCurrent}
                            stepId={currentStep}
                            isEnabled={isNextButtonEnabled}
                            isStepProcessing={isStepProcessing}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {isCompleted && !isCurrent && <div className="step-content-wrapper"><div className="step-content">{renderOverviewForStep(s.id)}</div></div>}
              </div>
            );
          })}
          <div className="always-display">{renderAlwaysDisplay()}</div>
          <div className="mobile-right-rail-steps">{renderMobileRightRailForSteps()}</div>
          <div className="step-footer">{renderFooter()}</div>
        </>
      )}
    </div>
  );
}

function StepsWrapper(props) {
  return (
    <StepsProvider {...props}>
      <Steps {...props} />
    </StepsProvider>
  );
}

Steps.propTypes = {
  schema: PropTypes.shape({
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.shape({
          value: PropTypes.string,
        }),
        components: PropTypes.arrayOf(PropTypes.string).isRequired,
        required: PropTypes.bool,
        errorMessage: PropTypes.shape({
          value: PropTypes.string,
        }),
        overviewComponent: PropTypes.string,
        submitButton: PropTypes.string,
      }),
    ).isRequired,
    summary: PropTypes.shape({
      components: PropTypes.arrayOf(PropTypes.string),
      submitButton: PropTypes.string,
    }),
    alwaysDisplay: PropTypes.shape({
      components: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
  initialStep: PropTypes.number,
};

Steps.defaultProps = {
  initialStep: 0,
};

export default StepsWrapper;
